/**
 * TRAC-732 follow-up — sales-credit attribution.
 *
 * When a customer connects to a live LivePerson agent, forward that agent's identity to the
 * host page (the tirerack.com parent frame) so the web team can store it for sales credit.
 *
 * Fires ONLY on the `agent_named` status — the point where the REAL rep identity is known.
 * It deliberately does NOT fire on `agent_joined`, where the name may still be the generic
 * "Tire Rack team" fallback. On a transfer the bridge emits a fresh `agent_named` for the
 * next rep, so this re-fires and the LAST connected agent gets credit (mirrors LP today).
 *
 * Payload contract (agreed with the web team):
 *   { source: 'TRACI', type: 'live_agent_connected', agentName, agentId, agentEmployeeId,
 *     sessionId, timestamp }
 * Credit is on `agentName`; `agentId` (LP login) + `agentEmployeeId` come off the same
 * LE-Users record the bridge resolves. Both are null until/unless the profile lookup resolves
 * them.
 */

export interface LiveAgentConnectedMessage {
  source: 'TRACI';
  type: 'live_agent_connected';
  agentName: string;
  agentId: string | null;         // LP loginName
  agentEmployeeId: string | null;
  sessionId: string | null;
  timestamp: string;
}

/**
 * Post `live_agent_connected` to the host page IFF this is a real-identity (`agent_named`)
 * event, we have a name, and we're embedded in a parent frame. Returns the message that was
 * posted (for logging / tests), or null when it was a no-op.
 */
export function postLiveAgentConnected(
  win: Window,
  state: string,
  agentName: string | undefined | null,
  sessionId: string | null,
  agentId?: string | null,
  agentEmployeeId?: string | null,
): LiveAgentConnectedMessage | null {
  // Only the real-identity event credits — never `agent_joined` (fallback name) or others.
  if (state !== 'agent_named') return null;
  const name = (agentName || '').trim();
  if (!name) return null; // nothing to attribute
  if (win.parent === win) return null; // not embedded — there's no host page to notify

  const message: LiveAgentConnectedMessage = {
    source: 'TRACI',
    type: 'live_agent_connected',
    agentName: name,
    agentId: agentId ?? null,
    agentEmployeeId: agentEmployeeId ?? null,
    sessionId,
    timestamp: new Date().toISOString(),
  };

  // NOTE: targetOrigin is '*' for now so SIT/dev parents can receive it.
  // TODO: lock this to the tirerack.com parent origin before prod.
  win.parent.postMessage(message, '*');
  return message;
}
